import { useState } from 'react'
import TicketTypeBadge from '../ticket/TicketTypeBadge'
import FileDrop from '../File/FileDrop'
import TicketTypePicker from './TicketTypePicker'
import OriginPicker from './OriginPicker'
import ImpactUrgencyPicker from './ImpactUrgencyPicker'
import { TICKET_ORIGINS } from '../../utils/ticketDraft'
import { getPriorityMeta } from '../../utils/priorityMatrix'
import { formatDuration, getPolicy } from '../../utils/sla'
import '../../styles/ticket-form.css'

/**
 * Resumen del ticket antes de enviarlo: muestra la clasificación y el tiempo
 * objetivo de atención, y permite corregir cualquier campo.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.draft - Borrador del ticket.
 * @param {(changes: Object) => void} props.onChange - Aplica cambios al borrador.
 * @param {Record<string, string>} [props.errors={}] - Errores por campo.
 * @param {(id: number) => string} props.priorityLabel
 * @param {Array<Object>} [props.files] - Adjuntos (si se pasan, se muestran).
 * @param {Function} [props.onFilesChange] - Si se pasa, permite adjuntar aquí.
 * @returns {JSX.Element}
 */
const TicketReview = ({
  draft,
  onChange,
  errors = {},
  priorityLabel,
  files,
  onFilesChange,
}) => {
  // Si falta la clasificación se abre el panel para que se vea el error.
  const [editClassification, setEditClassification] = useState(
    () => !draft.systemOrigin
  )
  const priority = getPriorityMeta(draft.priorityId)
  const policy = getPolicy(draft)
  const origin = TICKET_ORIGINS.find((o) => o.value === draft.systemOrigin)
  const showPanel = editClassification || errors.systemOrigin || errors.type

  return (
    <div className="tr-review d-flex flex-column gap-3">
      {/* CLASIFICACIÓN */}
      <div className="tr-summary">
        <div className="tr-chips">
          <TicketTypeBadge type={draft.type} />
          <span className="tr-chip">
            <i className={`fa-solid ${origin?.icon || 'fa-circle-question'}`} />
            {origin?.label || 'Sin plataforma'}
          </span>
          <span
            className="tr-chip"
            style={{ color: priority.color, background: `${priority.color}1a` }}
          >
            <i className="fa-solid fa-flag" />
            Prioridad {priorityLabel(draft.priorityId)}
          </span>
        </div>
        <p className="tr-sla mb-0">
          <i className="fa-regular fa-clock me-1" />
          {policy
            ? `Tiempo objetivo de resolución: ${formatDuration(policy.resolutionMins * 60000)}`
            : 'Este tipo de registro se planifica; no tiene tiempo de resolución fijo.'}
        </p>
        <button
          type="button"
          className="tr-edit"
          onClick={() => setEditClassification((v) => !v)}
          aria-expanded={!!showPanel}
        >
          <i className="fa-solid fa-pen me-1" />
          {showPanel ? 'Ocultar clasificación' : 'Ajustar clasificación'}
        </button>
      </div>

      {showPanel && (
        <div className="at-card d-flex flex-column gap-3">
          <div>
            <label className="at-label">Tipo de registro</label>
            <TicketTypePicker
              value={draft.type}
              onChange={(type) => onChange({ type })}
            />
          </div>
          <div>
            <label className="at-label">
              Plataforma / Origen <span className="at-req">*</span>
            </label>
            <OriginPicker
              value={draft.systemOrigin}
              onChange={(systemOrigin) => onChange({ systemOrigin })}
              invalid={!!errors.systemOrigin}
            />
            {errors.systemOrigin && (
              <p className="at-error-text">{errors.systemOrigin}</p>
            )}
          </div>
          <ImpactUrgencyPicker
            impact={draft.impact}
            urgency={draft.urgency}
            priorityId={draft.priorityId}
            priorityLabel={priorityLabel}
            onChange={onChange}
          />
        </div>
      )}

      {/* CONTENIDO */}
      <div className="at-card">
        <label className="at-label">
          Título <span className="at-req">*</span>
        </label>
        <input
          type="text"
          className={`form-control at-input ${errors.title ? 'is-invalid' : ''}`}
          value={draft.title}
          maxLength={150}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        {errors.title && <p className="at-error-text">{errors.title}</p>}

        <label className="at-label mt-3">
          Descripción <span className="at-req">*</span>
        </label>
        <textarea
          className={`form-control at-input ${errors.description ? 'is-invalid' : ''}`}
          rows="8"
          value={draft.description}
          onChange={(e) => onChange({ description: e.target.value })}
        />
        {errors.description && (
          <p className="at-error-text">{errors.description}</p>
        )}

        {onFilesChange ? (
          <div className="mt-3">
            <label className="at-label">Adjuntos (opcional)</label>
            <FileDrop files={files} setFiles={onFilesChange} />
          </div>
        ) : (
          files?.length > 0 && (
            <p className="tr-files mb-0 mt-3">
              <i className="fa-solid fa-paperclip me-1" />
              {files.length} archivo(s) adjunto(s)
            </p>
          )
        )}
      </div>

      <style>{`
        .tr-summary {
          display: flex; flex-direction: column; gap: 10px; padding: 16px 18px;
          border: 1px solid var(--gm-border); border-radius: 14px; background: var(--gm-surface);
        }
        .tr-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .tr-chip {
          display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
          font-size: 0.75rem; font-weight: 600; background: var(--gm-surface-2); color: var(--gm-text);
        }
        .tr-sla, .tr-files { font-size: 0.8rem; color: var(--gm-text-muted); }
        .tr-edit {
          align-self: flex-start; border: none; background: transparent; padding: 0;
          color: var(--gm-primary); font-size: 0.8rem; font-weight: 600;
        }
      `}</style>
    </div>
  )
}

export default TicketReview
